import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';

const RACE_OPTIONS = [
  { value: 1, label: 'E bardhë', colIndex: 0 }
];

function App() {
  const [formData, setFormData] = useState({
    race: 1,
    currentAge: 40,
    ageMenarche: 12,
    age1stBirth: 0,
    numRelatives: 0,
    numBiopsies: 0,
    hyperplasiaStatus: 1.0
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculate = async () => {
    if (formData.currentAge < 35 || formData.currentAge > 85) {
      alert('Mosha duhet të jetë midis 35 dhe 85');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/kalkulatori-gail/api/calculate.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Gabim në lidhje me serverin');
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setResult(data);
    } catch (err) {
      setError(err.message || 'Ka ndodhur një gabim. Ju lutemi provoni përsëri.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Vlerësimi i Rrezikut të Kancerit të Gjirit</h1>
          <p className="text-gray-600 mb-6">Kalkulatori i Modelit Gail</p>
          
          <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-md">
            <div className="flex items-start space-x-3">
              <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-2">Kujdes të Rëndësishëm:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Ky model është zhvilluar për popullatën amerikane dhe bazohet në të dhëna nga Shtetet e Bashkuara</li>
                  <li>Nuk përdor të dhëna specifike nga Kosova, Shqipëria, apo rajoni</li>
                  <li>Për popullata Rome, Ashkali dhe Egjiptase, opcioni "E bardhë" është përafërimi më i afërt demografik</li>
                  <li>Rezultatet duhet interpretohen me kujdes dhe diskutohen me profesionistë shëndetësorë</li>
                </ul>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
              <div className="flex items-start space-x-3">
                <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Raca/Etnia</label>
              <select 
                value={formData.race}
                onChange={(e) => handleChange('race', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                {RACE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mosha Aktuale (35-85)</label>
              <input 
                type="number"
                value={formData.currentAge}
                onChange={(e) => handleChange('currentAge', parseInt(e.target.value))}
                min="35"
                max="85"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mosha në Periodën e Parë Menstruale</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="radio"
                    name="ageMenarche"
                    value="10"
                    checked={formData.ageMenarche === 10}
                    onChange={(e) => handleChange('ageMenarche', parseInt(e.target.value))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">7 deri 11</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="radio"
                    name="ageMenarche"
                    value="12"
                    checked={formData.ageMenarche === 12}
                    onChange={(e) => handleChange('ageMenarche', parseInt(e.target.value))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">12 deri 13</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="radio"
                    name="ageMenarche"
                    value="14"
                    checked={formData.ageMenarche === 14}
                    onChange={(e) => handleChange('ageMenarche', parseInt(e.target.value))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">14 ose më shumë</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mosha në Lindje të Parë</label>
              <select 
                value={formData.age1stBirth}
                onChange={(e) => handleChange('age1stBirth', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">Asnjë lindje</option>
                <option value="18">Nën 20</option>
                <option value="22">20-24</option>
                <option value="27">25-29</option>
                <option value="30">30 ose më shumë</option>
                <option value="18">E panjohur</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numri i të Afërmve të Shkallës së Parë me Kancer të Gjirit (nëna, motrat ose bijat)
              </label>
              <select 
                value={formData.numRelatives}
                onChange={(e) => handleChange('numRelatives', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2 ose më shumë</option>
                <option value="0">E panjohur</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Numri i Biopsive të Mëparshme të Gjirit</label>
              <select 
                value={formData.numBiopsies}
                onChange={(e) => handleChange('numBiopsies', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2 ose më shumë</option>
              </select>
            </div>

            {formData.numBiopsies > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">A ka pasur biopsi me hiperplazi atipike?</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="radio"
                      name="hyperplasiaStatus"
                      value="1.82"
                      checked={formData.hyperplasiaStatus === 1.82}
                      onChange={(e) => handleChange('hyperplasiaStatus', parseFloat(e.target.value))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Po</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="radio"
                      name="hyperplasiaStatus"
                      value="0.93"
                      checked={formData.hyperplasiaStatus === 0.93}
                      onChange={(e) => handleChange('hyperplasiaStatus', parseFloat(e.target.value))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Jo</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="radio"
                      name="hyperplasiaStatus"
                      value="1.0"
                      checked={formData.hyperplasiaStatus === 1.0}
                      onChange={(e) => handleChange('hyperplasiaStatus', parseFloat(e.target.value))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">E panjohur</span>
                  </label>
                </div>
              </div>
            )}

            <button 
              onClick={handleCalculate}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Duke llogaritur...' : 'Llogarit Rrezikun'}
            </button>
          </div>
        </div>

        {result && (
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Rezultatet e Vlerësimit të Rrezikut</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Rreziku 5-Vjeçar</h3>
                <p className="text-3xl font-bold text-blue-600">{result.fiveYearRisk}%</p>
                <p className="text-sm text-gray-600 mt-1">Mesatare: {result.fiveYearAvg}%</p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Në një grup prej 100 grave me faktorë të ngjashëm rreziku, rreth {Math.round(parseFloat(result.fiveYearRisk))} 
                  {Math.round(parseFloat(result.fiveYearRisk)) === 1 ? ' grua' : ' gra'} pritet të zhvillojnë kancer invaziv të gjirit 
                  në 5 vitet e ardhshme.
                </p>
                {parseFloat(result.fiveYearRisk) > parseFloat(result.fiveYearAvg) && (
                  <p className="text-xs text-red-600 mt-2 font-medium">Mbi mesataren</p>
                )}
                {parseFloat(result.fiveYearRisk) < parseFloat(result.fiveYearAvg) && (
                  <p className="text-xs text-green-600 mt-2 font-medium">Nën mesataren</p>
                )}
                {parseFloat(result.fiveYearRisk) === parseFloat(result.fiveYearAvg) && (
                  <p className="text-xs text-blue-600 mt-2 font-medium">Në nivel të mesatares</p>
                )}
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Rreziku Gjatë Jetës (deri në moshën 90)</h3>
                <p className="text-3xl font-bold text-purple-600">{result.lifetimeRisk}%</p>
                <p className="text-sm text-gray-600 mt-1">Mesatare: {result.lifetimeAvg}%</p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Në një grup prej 100 grave me faktorë të ngjashëm rreziku, rreth {Math.round(parseFloat(result.lifetimeRisk))} 
                  {Math.round(parseFloat(result.lifetimeRisk)) === 1 ? ' grua' : ' gra'} pritet të zhvillojnë kancer invaziv të gjirit 
                  gjatë jetës (deri në moshën 90 vjeç).
                </p>
                {parseFloat(result.lifetimeRisk) > parseFloat(result.lifetimeAvg) && (
                  <p className="text-xs text-red-600 mt-2 font-medium">Mbi mesataren</p>
                )}
                {parseFloat(result.lifetimeRisk) < parseFloat(result.lifetimeAvg) && (
                  <p className="text-xs text-green-600 mt-2 font-medium">Nën mesataren</p>
                )}
                {parseFloat(result.lifetimeRisk) === parseFloat(result.lifetimeAvg) && (
                  <p className="text-xs text-blue-600 mt-2 font-medium">Në nivel të mesatares</p>
                )}
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-md flex items-start space-x-3">
              <AlertCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1">Shënime të Rëndësishme:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Ky mjet është për qëllime edukative të bazuara në Modelin Gail</li>
                  <li>Rezultatet tregojnë probabilitetin e zhvillimit të kancerit invaziv të gjirit</li>
                  <li>Nuk përfshin DCIS (karcinomë duktale in situ)</li>
                  <li>Konsultohuni me ofruesit e kujdesit shëndetësor për vlerësim të personalizuar të rrezikut</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Rreth Modelit Gail</h3>
              <div className="text-sm text-gray-700 space-y-3">
                <p>
                  Mjeti i Vlerësimit të Rrezikut të Kancerit të Gjirit (BCRAT) bazohet në një model statistikor të njohur si Modeli Gail, 
                  i emëruar sipas Dr. Mitchell Gail, Hetues i Lartë në Degën e Biostatistikës të Divizionit të NCI për Epidemiologji dhe Gjenetikë të Kancerit.
                </p>
                <p className="font-medium">Modeli përdor të dhëna personale për të vlerësuar rrezikun e zhvillimit të kancerit invaziv të gjirit duke përfshirë:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 text-gray-600">
                  <li>Moshën</li>
                  <li>Moshën në fillimin e menstruacionit</li>
                  <li>Moshën në lindjen e parë të një fëmije</li>
                  <li>Numrin e të afërmve të shkallës së parë (nënë, motra, vajza) me kancer të gjirit</li>
                  <li>Numrin e biopsiave të mëparshme të gjirit</li>
                  <li>Praninë e hiperplazisë atipike në biopsi</li>
                </ul>
                
                <div className="mt-4 pt-4 border-t border-gray-300">
                  <p className="font-medium mb-2">Burimet e të Dhënave:</p>
                  <p className="text-gray-600">
                    Të dhënat nga Projekti i Demonstrimit të Detektimit të Kancerit të Gjirit (BCDDP), një studim i përbashkët i NCI 
                    dhe Shoqatës Amerikane të Kancerit që përfshinte 280,000 gra të bardha të moshës 35 deri 74 vjeç, dhe nga 
                    Programi SEER (Mbikëqyrje, Epidemiologji dhe Rezultate Përfundimtare) i NCI, u përdorën për zhvillimin e modelit.
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-300">
                  <p className="font-medium mb-2">Testimi i Modelit:</p>
                  <p className="text-gray-600">
                    Modeli Gail është testuar në popullata të mëdha të grave të bardha dhe është treguar se ofron vlerësime të sakta 
                    të rrezikut të kancerit të gjirit. Modeli u përditësua për herë të fundit në dhjetor 2017 (versioni 4.1).
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-300">
                  <p className="font-medium mb-2">Referenca Kryesore:</p>
                  <div className="text-xs text-gray-600 space-y-2">
                    <p>
                      1. Gail MH, Brinton LA, Byar DP, et al. Projecting individualized probabilities of developing breast cancer 
                      for White females who are being examined annually. <em>J Natl Cancer Inst</em> 81(24):1879-86, 1989.
                    </p>
                    <p>
                      2. Costantino JP, Gail MH, Pee D, et al. Validation studies for models projecting the risk of invasive and 
                      total breast cancer incidence. <em>J Natl Cancer Inst</em> 91(18):1541-8, 1999.
                    </p>
                    <p>
                      3. Gail MH, Costantino JP, Bryant J, et al. Weighing the risks and benefits of tamoxifen treatment for 
                      preventing breast cancer. <em>J Natl Cancer Inst</em> 91(21):1829-46, 1999.
                    </p>
                    <p>
                      4. Gail MH, Costantino JP, Pee D, et al. Projecting Individualized Absolute Invasive Breast Cancer Risk in 
                      African American Women. <em>J Natl Cancer Inst</em> 99(23):1782-1792, 2007.
                    </p>
                    <p>
                      5. Matsuno RK, Costantino JP, Ziegler RG, et al. Projecting Individualized Absolute Invasive Breast Cancer 
                      Risk in Asian and Pacific Island American Women. <em>J Natl Cancer Inst</em> 2011.
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-300">
                  <p className="text-xs text-gray-500 italic">
                    Burimi: National Cancer Institute (NCI) - Breast Cancer Risk Assessment Tool
                    <br />
                    Përshtatur për popullatën shqiptare për qëllime edukative nga Instituti për Praktika Racionale Mjekësore
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;